import React from 'react';
import { Settings, Paintbrush, Sparkles, Trash2, Copy, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * BlockTabs - Chrome-style tabs connected to content panel
 * Active tab visually merges into the content below
 */

const rightTabs = [
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'style', label: 'Style', icon: Paintbrush },
  { id: 'animation', label: 'Animation', icon: Sparkles },
];

export const BlockTabs = ({ 
  activeTab, 
  onTabChange, 
  disabledTabs = [],
  // Quick Actions props
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onTogglePublished,
  isFirst = false,
  isLast = false,
  isPublished = true,
  showQuickActions = true,
}) => {
  return (
    <div className="relative -mx-4 px-4 mb-0">
      <div className="flex items-center justify-between">
        {/* Quick Actions - Left side */}
        {showQuickActions && (
          <div className="flex items-center gap-1 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onMoveUp}
              disabled={isFirst}
              className={`h-7 w-7 p-0 ${isFirst ? 'text-slate-300' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}
              title="Move up"
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMoveDown}
              disabled={isLast}
              className={`h-7 w-7 p-0 ${isLast ? 'text-slate-300' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}
              title="Move down"
            >
              <ArrowDown size={14} />
            </Button>
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onTogglePublished}
              className={`h-7 w-7 p-0 ${isPublished ? 'text-emerald-500 hover:text-emerald-700' : 'text-amber-500 hover:text-amber-700'}`}
              title={isPublished ? "Hide block" : "Show block"}
            >
              {isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDuplicate}
              className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
              title="Duplicate"
            >
              <Copy size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="h-7 w-7 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        )}
        
        {/* Tab Navigation - Chrome-style tabs */}
        <div className="flex items-end">
          {rightTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isDisabled = disabledTabs.includes(tab.id);
            
            return (
              <button
                key={tab.id}
                onClick={() => !isDisabled && onTabChange(tab.id)}
                disabled={isDisabled}
                data-testid={`block-tab-${tab.id}`}
                className={`
                  relative flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all
                  ${isActive 
                    ? 'bg-white text-indigo-700 border border-slate-200 border-b-white rounded-t-lg -mb-px z-10' 
                    : isDisabled
                      ? 'text-slate-300 cursor-not-allowed px-3 py-1.5 mb-0.5'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-t-lg px-3 py-1.5 mb-0.5'
                  }
                `}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Bottom border line - the active tab's border-b-white punches through this */}
      <div className="border-b border-slate-200" />
    </div>
  );
};

/**
 * BlockSplitPane - Two-column layout with content always visible
 * @param {React.ReactNode} contentPane - The main content (always visible on left)
 * @param {React.ReactNode} settingsPane - Settings/Style/Animation content (shown on right when tab selected)
 * @param {boolean} showRightPane - Whether to show the right pane
 */
export const BlockSplitPane = ({ contentPane, settingsPane, showRightPane = false }) => {
  if (!showRightPane) {
    // Full width content when no settings tab is selected
    return <div className="w-full">{contentPane}</div>;
  }
  
  return (
    <div className="flex gap-4 min-h-[120px]">
      {/* Left: Content/Preview - always visible */}
      <div className="flex-1 min-w-0 border-r border-slate-100 pr-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Content</div>
        {contentPane}
      </div>
      
      {/* Right: Settings panel */}
      <div className="w-[320px] flex-shrink-0">
        {settingsPane}
      </div>
    </div>
  );
};

/**
 * BlockTabContent - Panel container for tab content
 * Styled to connect visually with the active Chrome-style tab above
 */
export const BlockTabContent = ({ children, isActive }) => {
  if (!isActive) return null;
  return (
    <div className="border border-t-0 border-slate-200 rounded-b-lg bg-white p-4 mb-2">
      {children}
    </div>
  );
};

export default BlockTabs;
